import axios from "axios";
import { WORKSPACE_HEADER, workspaceScopeOf } from "@/lib/workspaceBinding";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Session epoch: bumped whenever the signed-in identity changes (login, logout, expiry).
// A 401 only expires the session it was sent with, so late responses cannot end a newer one.
let sessionEpoch = 0;
let onSessionExpired = null;
export const beginSessionEpoch = () => ++sessionEpoch;
export function setSessionExpiredHandler(handler) {
  onSessionExpired = handler;
  return () => { if (onSessionExpired === handler) onSessionExpired = null; };
}
// Credential endpoints answer 401 for a wrong password or a missing session; that is not an expiry.
const SESSION_EXEMPT = /^\/auth\/(login|register|logout|forgot-password|reset-password)(?:[/?]|$)/;

// D10: the workspace this tab displays ("personal" or a workspace id), set by AuthContext whenever its user changes.
// Every request (read or write) states the workspace of the page at the moment it is created; the server refuses it
// (409 workspace_changed) if the current workspace changed elsewhere meanwhile, and refuses a request that states none
// (400 workspace_binding_required). `workspaceEpoch` counts the changes of displayed workspace in this tab, so that an
// answer arriving after such a change is never applied to the new workspace.
export { WORKSPACE_HEADER, workspaceScopeOf };
// Identity and workspace choice: their answers describe the account, not the workspace a page displays.
const ACCOUNT_LEVEL = /^\/(auth|workspaces)(?:[/?]|$)/;
let workspaceScope = null;
let workspaceEpoch = 0;
let onWorkspaceChanged = null;
export function setWorkspaceScope(user) {
  const next = workspaceScopeOf(user);
  const key = `${user?.id || ""}|${next || ""}`;
  if (key !== setWorkspaceScope.key) { setWorkspaceScope.key = key; workspaceEpoch += 1; }
  workspaceScope = next;
}
export function setWorkspaceChangedHandler(handler) {
  onWorkspaceChanged = handler;
  return () => { if (onWorkspaceChanged === handler) onWorkspaceChanged = null; };
}
export const isWorkspaceChanged = (error) => error?.response?.status === 409 && error?.response?.data?.detail?.code === "workspace_changed";
// A request answered after this tab moved to another workspace: its result belongs to the previous one.
export const SCOPE_CHANGED = "SCOPE_CHANGED";
const scopeChanged = (cause) => Object.assign(new Error("L’espace affiché a changé pendant l’opération : son résultat n’est pas appliqué ici. Vérifiez-le dans l’espace d’origine."), { code: SCOPE_CHANGED, cause, config: cause?.config });

// Attach bearer token as a fallback (cookies are the primary mechanism). Synchronous: the token, the session epoch and
// the workspace are those of the moment the request is created, not of a later microtask.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("op_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.sessionEpoch = sessionEpoch;
  config.workspaceEpoch = workspaceEpoch;
  if (workspaceScope && !config.headers[WORKSPACE_HEADER]) config.headers[WORKSPACE_HEADER] = workspaceScope;
  return config;
}, undefined, { synchronous: true });

// Only an authenticated request rejected with 401 in the current epoch signals an invalid session.
// Network errors, 403 and 5xx pass through untouched; nothing is retried here.
const stale = (config) => !!config && config.workspaceEpoch !== workspaceEpoch && !ACCOUNT_LEVEL.test(config.url || "");
api.interceptors.response.use((response) => (stale(response?.config) ? Promise.reject(scopeChanged(response)) : response), (error) => {
  const config = error?.config;
  if (error?.response?.status === 401 && config && !SESSION_EXEMPT.test(config.url || "")
      && config.sessionEpoch === sessionEpoch && onSessionExpired) {
    onSessionExpired(error);
  }
  if (stale(config) && error?.response?.status !== 401) return Promise.reject(scopeChanged(error));
  // This tab still shows the workspace the server no longer has as current: say so once, centrally.
  if (isWorkspaceChanged(error) && config?.workspaceEpoch === workspaceEpoch && onWorkspaceChanged) {
    onWorkspaceChanged(error.response.data.detail);
  }
  return Promise.reject(error);
});

// Statuses the write routes used here raise before any write: 400 (no fields), 403 (viewer or private
// mode), 404 (private mode closed route, or no matching document), 422 (request validation).
const ESTABLISHED_REFUSALS = new Set([400, 403, 404, 422]);

// How a failed write ended. "session": 401, handled by the central handler. "refused": an established
// rejection without effect, including D10's 409 workspace_changed (checked before any write). "conflict": a 409
// whose detail says the server refused it before any write because the data changed since it was read (Saved,
// OP-026). "unknown": no answer, 5xx, any other status (408, other 409s, 429…), or an answer that arrived after
// this tab changed workspace (SCOPE_CHANGED), so the write may have taken effect before the error.
export function mutationFailure(error) {
  const status = error?.response?.status;
  if (error?.code === SCOPE_CHANGED) return "unknown";
  if (status === 401) return "session";
  if (ESTABLISHED_REFUSALS.has(status) || isWorkspaceChanged(error)) return "refused";
  if (status === 409 && error?.response?.data?.detail?.conflict === true) return "conflict";
  return "unknown";
}

export function formatError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail.message === "string") return detail.message;
  return String(detail);
}

export default api;
